import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence, useReducedMotion, useInView } from "framer-motion";
import {
  ShieldCheck, FileText, Gauge, Lock, ArrowRight, Check, X, Menu,
  Building2, Truck, HeartPulse, HardHat, UtensilsCrossed, Factory, Users, Nfc,
  ScanLine, Clock, Smartphone, Cpu, BadgeCheck, Ban, Timer,
  FileCheck2, TrendingUp, Cable, ChevronRight, MapPin, Coffee,
  AlertTriangle, KeyRound,
  FileSignature, GraduationCap, Stamp, Ticket, PlaneTakeoff, DoorOpen,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { HeroBackground } from "@/components/HeroBackground";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Seo } from "@/components/Seo";
import { PflegeSection } from "@/components/PflegeSection";

/* ------------------------------------------------------------------ helpers */
const fade = (d = 0) => ({
  initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" }, transition: { duration: 0.55, delay: d },
});

function CountUp({ to, suffix = "", decimals = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const reduce = useReducedMotion();
  const [v, setV] = useState(0);
  useEffect(() => {
    if (!inView) return;
    if (reduce) { setV(to); return; }
    let raf, start;
    const dur = 1100;
    const step = (t) => {
      start = start || t;
      const p = Math.min(1, (t - start) / dur);
      setV(to * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, reduce]);
  return <span ref={ref}>{v.toLocaleString("de-DE", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}{suffix}</span>;
}

const Badge = ({ children, tone = "cyan" }) => {
  const tones = {
    cyan: "border-cyan/30 bg-cyan/10 text-cyan-dark",
    amber: "border-amber-300/50 bg-amber-50 text-amber-700",
    emerald: "border-emerald-300/50 bg-emerald-50 text-emerald-700",
    slate: "border-slate-300/60 bg-slate-100 text-slate-600",
  };
  return <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${tones[tone]}`}>{children}</span>;
};

const SectionHead = ({ overline, title, sub, dark, center }) => (
  <div className={`${center ? "mx-auto text-center" : ""} max-w-2xl`}>
    <div className={`overline ${dark ? "text-cyan" : "text-cyan-dark"}`}>{overline}</div>
    <h2 className={`mt-2 font-heading text-3xl font-extrabold tracking-tight sm:text-4xl ${dark ? "text-white" : "text-navy"}`}>{title}</h2>
    {sub && <p className={`mt-3 text-base ${dark ? "text-slate-300" : "text-slate-500"}`}>{sub}</p>}
  </div>
);

/* ------------------------------------------------------------------ header */
const NAV_KEYS = [
  ["platform", "/#platform", false], ["fw", "/foreign-workforce", true],
  ["sub", "/subunternehmer", true], ["zeit", "/zeiterfassung", true],
  ["pflege", "/#pflege", false],
  ["baustellen", "/#baustellen", false],
  ["how", "/#how", false], ["hw", "/hardware", true],
  ["sec", "/#security", false], ["pricing", "/#pricing", false],
];

export function Header() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const Item = ({ k, href, route, scrolledCls, onClick }) => {
    const cls = `text-sm font-medium transition-colors ${scrolledCls ? "text-slate-600 hover:text-navy" : "text-slate-200 hover:text-white"}`;
    return route
      ? <Link to={href} className={cls} onClick={onClick}>{t(`lp.nav.${k}`)}</Link>
      : <a href={href} className={cls} onClick={onClick}>{t(`lp.nav.${k}`)}</a>;
  };
  return (
    <header className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${scrolled ? "glass-nav border-b border-white/40 shadow-sm" : "border-b border-transparent"}`}>
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5">
        <Link to="/" data-testid="nav-logo" className="shrink-0"><Logo variant={scrolled ? "dark" : "light"} /></Link>
        <nav className="mx-3 hidden flex-1 items-center justify-center gap-x-4 whitespace-nowrap text-[13px] 2xl:flex">
          {NAV_KEYS.map(([k, href, route]) => <Item key={href} k={k} href={href} route={route} scrolledCls={scrolled} />)}
        </nav>
        <div className="hidden shrink-0 items-center gap-3 2xl:flex">
          <LanguageSwitcher variant={scrolled ? "dark" : "light"} />
          <Link to="/login"><Button variant="ghost" data-testid="nav-login-btn" className={scrolled ? "" : "text-white hover:bg-white/10 hover:text-white"}>{t("lp.nav.login")}</Button></Link>
          <Link to="/register"><Button data-testid="nav-getstarted-btn" className="rounded-full bg-cyan px-5 text-white transition-transform hover:bg-cyan-dark hover:scale-[1.02] active:scale-[0.98]">{t("lp.nav.get")}</Button></Link>
        </div>
        <div className="flex shrink-0 items-center gap-2 2xl:hidden">
          <LanguageSwitcher variant={scrolled ? "dark" : "light"} />
          <button className={scrolled ? "text-navy" : "text-white"} onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle" aria-label="Menu">{open ? <X /> : <Menu />}</button>
        </div>
      </div>
      {open && (
        <div className="glass-nav border-t border-white/40 px-5 py-4 2xl:hidden">
          <div className="flex flex-col gap-3">
            {NAV_KEYS.map(([k, href, route]) => <Item key={href} k={k} href={href} route={route} scrolledCls onClick={() => setOpen(false)} />)}
            <div className="flex gap-3 pt-2">
              <Link to="/login" className="flex-1"><Button variant="outline" className="w-full">{t("lp.nav.login")}</Button></Link>
              <Link to="/register" className="flex-1"><Button className="w-full bg-cyan text-white">{t("lp.nav.get")}</Button></Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

/* ------------------------------------------------------------------ hero */
function FloatCard({ className, delay = 0, children, testid }) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      initial={{ opacity: 0, y: 16, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.6, delay }}
      className={`absolute z-20 rounded-2xl border border-white/15 bg-white/10 p-3 text-white backdrop-blur-xl premium-shadow ${!reduce ? "float-y" : ""} ${className}`}
      style={{ animationDelay: `${delay}s` }}
      data-testid={testid}
    >{children}</motion.div>
  );
}

const HERO_WORKERS = [
  ["Amir Hadžić", "RHEINWORK_Worker_0028", "06:57"],
  ["Marko Petrović", "ELEKTROBALKAN_Worker_0014", "07:02"],
  ["Kenan Begić", "RHEINWORK_Worker_0041", "07:05"],
  ["Ivana Jurić", "BAUPARTNER_Worker_0009", "07:11"],
  ["Stefan Ilić", "RHEINWORK_Worker_0033", "07:14"],
];

function HeroDashboard() {
  const { t } = useTranslation();
  const reduce = useReducedMotion();
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (reduce) return;
    const iv = setInterval(() => setIdx((i) => (i + 1) % HERO_WORKERS.length), 2600);
    return () => clearInterval(iv);
  }, [reduce]);
  const rows = [HERO_WORKERS[idx], HERO_WORKERS[(idx + 1) % HERO_WORKERS.length]];
  return (
    <div className="relative">
      <motion.div {...fade(0.15)} className="glass-dark rounded-2xl p-5 premium-shadow" data-testid="hero-dashboard">
        <div className="flex items-center justify-between">
          <div>
            <div className="overline text-cyan">{t("lp.dash.role")}</div>
            <div className="mt-1 font-heading text-lg font-bold text-white">RheinWork GmbH</div>
          </div>
          <ShieldCheck className="h-6 w-6 text-cyan" />
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2">
          {[[t("lp.dash.employees"), 84, "text-white"], [t("lp.dash.compliant"), 37, "text-emerald-400"], [t("lp.dash.action"), 7, "text-amber-400"]].map(([l, v, c]) => (
            <div key={l} className="rounded-xl bg-white/5 p-3">
              <div className={`font-heading text-2xl font-extrabold ${c}`}><CountUp to={v} /></div>
              <div className="mt-1 text-[10px] text-slate-400">{l}</div>
            </div>
          ))}
        </div>
        <div className="mt-4">
          <div className="mb-1.5 flex items-center justify-between text-[10px] uppercase tracking-wider text-slate-400">
            <span>{t("lp.dash.compliant")}</span><span className="text-emerald-400">82%</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
            <motion.div className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-cyan"
              initial={{ width: reduce ? "82%" : "0%" }} whileInView={{ width: "82%" }}
              viewport={{ once: true }} transition={{ duration: 1.2, ease: "easeOut", delay: 0.3 }} />
          </div>
        </div>
        <div className="mt-4 rounded-xl border border-white/10 bg-navy/40 p-3">
          <div className="mb-2 flex items-center justify-between text-[11px] text-slate-400"><span>{t("lp.dash.live")}</span><span className="flex items-center gap-1 text-emerald-400"><span className="live-dot inline-block h-1.5 w-1.5 rounded-full bg-emerald-400" /> 48 {t("lp.dash.working")}</span></div>
          <div className="relative min-h-[92px]">
            <AnimatePresence mode="popLayout" initial={false}>
              {rows.map((r) => (
                <motion.div key={r[1]} layout
                  initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.45, ease: "easeOut" }}
                  className="flex items-center justify-between border-t border-white/5 py-2 text-[11px]">
                  <div><div className="font-semibold text-white">{r[0]}</div><div className="font-mono text-[10px] text-slate-500">{r[1]}</div></div>
                  <div className="text-right"><div className="font-mono text-cyan">{r[2]}</div><div className="text-[9px] uppercase tracking-wider text-emerald-400">{t("lp.dash.working")}</div></div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>

      <FloatCard className="-left-6 top-6 hidden sm:block" delay={0.4} testid="hero-float-worker">
        <div className="text-[9px] uppercase tracking-wider text-slate-300">{t("lp.float.wc")}</div>
        <div className="font-heading text-lg font-extrabold text-emerald-400">37 <span className="text-xs font-medium text-slate-300">{t("lp.float.wcv")}</span></div>
      </FloatCard>
      <FloatCard className="-right-4 top-0 hidden sm:block" delay={0.6} testid="hero-float-sub">
        <div className="text-[9px] uppercase tracking-wider text-slate-300">{t("lp.float.su")}</div>
        <div className="font-heading text-lg font-extrabold text-white">12 <span className="text-xs font-medium text-slate-300">{t("lp.float.suv")}</span></div>
      </FloatCard>
      <FloatCard className="-right-8 top-1/2 hidden md:block" delay={0.8} testid="hero-float-nfc">
        <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-wider text-slate-300"><Nfc className="h-3 w-3 text-cyan" /> NFC</div>
        <div className="font-mono text-sm font-bold text-cyan">EG-000184</div>
      </FloatCard>
      <FloatCard className="-left-8 bottom-16 hidden md:block" delay={1} testid="hero-float-a1">
        <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-wider text-slate-300"><AlertTriangle className="h-3 w-3 text-amber-400" /> A1</div>
        <div className="font-heading text-sm font-bold text-amber-400">{t("lp.float.a1v")}</div>
      </FloatCard>
      <FloatCard className="-left-4 -bottom-6 hidden sm:block" delay={1.15} testid="hero-float-time">
        <div className="text-[9px] uppercase tracking-wider text-slate-300">{t("lp.float.tt")}</div>
        <div className="font-mono text-sm font-bold text-white">06:57 <span className="text-[10px] text-slate-400">{t("lp.float.ttv")}</span></div>
      </FloatCard>
      <FloatCard className="-right-6 -bottom-4 hidden sm:block" delay={1.3} testid="hero-float-margin">
        <div className="text-[9px] uppercase tracking-wider text-slate-300">{t("lp.float.pm")}</div>
        <div className="font-heading text-lg font-extrabold text-cyan">36.4%</div>
      </FloatCard>
    </div>
  );
}

function Hero() {
  const { t } = useTranslation();
  return (
    <section className="hero-navy relative pb-28 pt-28 sm:pt-36">
      <HeroBackground />
      <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-5 lg:grid-cols-2">
        <div>
          <motion.div {...fade(0)} className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-medium text-slate-300">
            <span className="h-1.5 w-1.5 rounded-full bg-cyan" /> {t("lp.hero.badge")}
          </motion.div>
          <motion.h1 {...fade(0.05)} className="mt-5 font-heading text-4xl font-extrabold leading-[1.03] tracking-tight text-white sm:text-5xl lg:text-6xl">
            {t("lp.hero.t1")}<br />{t("lp.hero.t2")}<br /><span className="bg-gradient-to-r from-cyan to-teal bg-clip-text text-transparent">{t("lp.hero.t3")}</span>
          </motion.h1>
          <motion.p {...fade(0.1)} className="mt-5 max-w-xl text-base text-slate-300 sm:text-lg">{t("lp.hero.sub")}</motion.p>
          <motion.div {...fade(0.15)} className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Link to="/register"><Button size="lg" data-testid="hero-primary-cta" className="w-full rounded-full bg-cyan px-7 text-white transition-transform hover:bg-cyan-dark hover:scale-[1.02] active:scale-[0.98] sm:w-auto">{t("lp.hero.primary")} <ArrowRight className="ml-1 h-4 w-4" /></Button></Link>
            <a href="#platform"><Button size="lg" variant="outline" data-testid="hero-secondary-cta" className="w-full rounded-full border-white/20 bg-transparent px-7 text-white hover:bg-white/10 hover:text-white sm:w-auto">{t("lp.hero.secondary")}</Button></a>
          </motion.div>
          <motion.div {...fade(0.22)} className="mt-6 flex flex-wrap gap-2" data-testid="hero-product-badges">
            {[t("lp.nav.fw"), t("lp.nav.sub"), t("lp.nav.zeit"), t("lp.nav.pflege")].map((b) => (
              <span key={b} className="rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-medium text-slate-200">{b}</span>
            ))}
          </motion.div>
        </div>
        <div className="relative pr-2"><HeroDashboard /></div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ modules */
function Modules() {
  const { t } = useTranslation();
  const MotionLink = motion(Link);
  const mods = [
    { id: "foreign-workforce", icon: Gauge, badge: <Badge>{t("lp.mod.badgeCore")}</Badge>, title: t("lp.mod.m1t"), desc: t("lp.mod.m1d"), cta: t("lp.mod.m1c"), accent: "from-navy to-cyan-dark" },
    { id: "subunternehmer", icon: Building2, badge: <Badge tone="amber">{t("lp.mod.badgeSub")}</Badge>, title: t("lp.mod.m2t"), desc: t("lp.mod.m2d"), cta: t("lp.mod.m2c"), accent: "from-teal to-cyan" },
    { id: "zeiterfassung", icon: Clock, badge: <Badge tone="amber">{t("lp.mod.badgeZeit")}</Badge>, title: t("lp.mod.m3t"), desc: t("lp.mod.m3d"), cta: t("lp.mod.m3c"), accent: "from-cyan-dark to-navy" },
    { id: "pflege", to: "#pflege", icon: HeartPulse, badge: <Badge tone="amber">Premium · 3.500 €/Jahr</Badge>, title: t("lp.mod.m4t"), desc: t("lp.mod.m4d"), cta: t("lp.mod.m4c"), accent: "from-teal-600 to-cyan-dark" },
  ];
  return (
    <section id="platform" className="mesh-soft py-24">
      <div className="mx-auto max-w-7xl px-5">
        <SectionHead overline={t("lp.mod.overline")} title={t("lp.mod.title")} sub={t("lp.mod.sub")} />
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {mods.map((m, i) => (
            <MotionLink key={m.id} to={m.to || `/${m.id}`} {...fade(i * 0.08)}
              className="group card-soft flex flex-col p-7 transition-transform hover:-translate-y-1" data-testid={`module-card-${m.id}`}>
              <div className="flex items-center justify-between">
                <div className={`flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br ${m.accent} text-white transition-transform group-hover:scale-105`}><m.icon className="h-6 w-6" strokeWidth={1.75} /></div>
                {m.badge}
              </div>
              <h3 className="mt-5 font-heading text-xl font-bold text-navy">{m.title}</h3>
              <p className="mt-2 flex-1 text-sm text-slate-500">{m.desc}</p>
              <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-cyan-dark transition-all group-hover:gap-2">{m.cta} <ArrowRight className="h-4 w-4" /></span>
            </MotionLink>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ foreign workforce */
function ForeignWorkforce() {
  const { t } = useTranslation();
  const feats = t("lp.fw.feats", { returnObjects: true });
  return (
    <section id="foreign-workforce" className="mx-auto max-w-7xl px-5 py-24">
      <div className="grid items-center gap-14 lg:grid-cols-2">
        <motion.div {...fade(0)}>
          <Badge>{t("lp.fw.badge")}</Badge>
          <h2 className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">{t("lp.fw.title")}</h2>
          <p className="mt-4 text-slate-500">{t("lp.fw.desc")}</p>
          <div className="mt-6 grid grid-cols-2 gap-2">
            {feats.map((f) => <div key={f} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600"><Check className="h-4 w-4 text-teal" /> {f}</div>)}
          </div>
          <Link to="/register"><Button className="mt-7 rounded-full bg-navy text-white hover:bg-navy-muted" data-testid="fw-cta">{t("lp.fw.cta")} <ArrowRight className="ml-1 h-4 w-4" /></Button></Link>
        </motion.div>
        <motion.div {...fade(0.1)} className="card-soft overflow-hidden p-0 premium-shadow">
          <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-4 py-2.5">
            <span className="h-2.5 w-2.5 rounded-full bg-red-300" /><span className="h-2.5 w-2.5 rounded-full bg-amber-300" /><span className="h-2.5 w-2.5 rounded-full bg-emerald-300" />
            <span className="ml-2 text-xs font-semibold text-slate-500">{t("lp.fw.record")}</span>
          </div>
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-cyan to-teal font-bold text-white">AH</div>
                <div><div className="font-semibold text-navy">Amir Hadžić</div><div className="font-mono text-[11px] text-slate-400">RHEINWORK_Worker_0028</div></div>
              </div>
              <Badge tone="amber"><AlertTriangle className="h-3 w-3" /> {t("lp.fw.actionReq")}</Badge>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
              {[[t("lp.fw.nationality"), "Bosnia & Herzegovina"], [t("lp.fw.workAuth"), t("lp.fw.workAuthVal")], [t("lp.fw.residence"), "18.11.2026"], [t("lp.fw.compliance"), t("lp.fw.complianceVal")]].map(([k, v], i) => (
                <div key={k} className="group rounded-xl bg-slate-50 px-3 py-2.5 transition-colors hover:bg-cyan/5">
                  <div className="text-[11px] text-slate-400">{k}</div>
                  <div className={`font-semibold ${i === 1 ? "text-emerald-600" : i === 3 ? "text-cyan-dark" : "text-navy"}`}>{v}</div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ subunternehmer */
function Subunternehmer() {
  const { t } = useTranslation();
  const steps = t("lp.sub.steps", { returnObjects: true });
  const feats = t("lp.sub.feats", { returnObjects: true });
  return (
    <section id="subunternehmer" className="bg-slate-50 py-24">
      <div className="mx-auto max-w-7xl px-5">
        <div className="grid items-center gap-14 lg:grid-cols-2">
          <motion.div {...fade(0)}>
            <div className="flex items-center gap-2"><Badge tone="amber">{t("lp.sub.badge")}</Badge><span className="font-heading text-sm font-bold text-navy">{t("lp.sub.perYear")}</span></div>
            <h2 className="mt-3 font-heading text-3xl font-extrabold leading-tight tracking-tight text-navy sm:text-4xl">{t("lp.sub.t1")}<br />{t("lp.sub.t2")}<br /><span className="text-cyan-dark">{t("lp.sub.t3")}</span></h2>
            <p className="mt-4 text-slate-500">{t("lp.sub.desc")}</p>
          </motion.div>
          <motion.div {...fade(0.1)} className="grid gap-4 sm:grid-cols-2">
            <div className="card-soft p-5">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">{t("lp.sub.scLabel")}</div>
              <div className="mt-1 font-heading text-lg font-bold text-navy">Elektro Balkan d.o.o.</div>
              <div className="mt-3 space-y-1.5 text-sm text-slate-600">
                <div className="flex justify-between"><span className="text-slate-400">{t("lp.sub.country")}</span><span>Slovenia</span></div>
                <div className="flex justify-between"><span className="text-slate-400">{t("lp.sub.workers")}</span><span className="font-semibold">18</span></div>
                <div className="flex justify-between"><span className="text-slate-400">{t("lp.sub.baustellen")}</span><span className="font-semibold">2</span></div>
              </div>
              <div className="mt-3"><Badge tone="amber"><AlertTriangle className="h-3 w-3" /> {t("lp.sub.actionReq")}</Badge></div>
            </div>
            <div className="space-y-4">
              <div className="card-soft p-4">
                <div className="text-xs text-slate-400">{t("lp.sub.freistellung")}</div>
                <div className="mt-1 flex items-center gap-1 text-sm font-semibold text-emerald-600"><BadgeCheck className="h-4 w-4" /> {t("lp.sub.validUntil")}</div>
                <div className="mt-2 text-xs text-slate-400">{t("lp.sub.responsible")}</div>
                <div className="text-sm font-medium text-navy">Finanzamt Oranienburg</div>
              </div>
              <div className="card-soft grid grid-cols-3 gap-2 p-4 text-center">
                <div><div className="font-heading text-lg font-bold text-emerald-600">14</div><div className="text-[10px] text-slate-400">{t("lp.sub.a1Valid")}</div></div>
                <div><div className="font-heading text-lg font-bold text-amber-600">3</div><div className="text-[10px] text-slate-400">{t("lp.sub.expiring")}</div></div>
                <div><div className="font-heading text-lg font-bold text-red-600">1</div><div className="text-[10px] text-slate-400">{t("lp.sub.missing")}</div></div>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="mt-14 overflow-x-auto">
          <div className="flex min-w-max items-center gap-3" data-testid="sub-workflow">
            {steps.map((s, i) => (
              <div key={s} className="flex items-center gap-3">
                <motion.div {...fade(i * 0.06)} className="w-40 rounded-2xl border border-slate-200 bg-white p-4 premium-shadow">
                  <div className="font-mono text-xs font-bold text-cyan-dark">0{i + 1}</div>
                  <div className="mt-1 text-sm font-semibold text-navy">{s}</div>
                </motion.div>
                {i < steps.length - 1 && <ChevronRight className="h-5 w-5 shrink-0 text-slate-300" />}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {feats.map(([tt, d], i) => (
            <motion.div key={tt} {...fade(i * 0.04)} className="card-soft p-5">
              <div className="font-heading text-sm font-bold text-navy">{tt}</div>
              <div className="mt-1 text-xs text-slate-500">{d}</div>
            </motion.div>
          ))}
        </div>
        <div className="mt-10 flex flex-col items-center gap-2">
          <Link to="/register"><Button className="rounded-full bg-cyan px-7 text-white hover:bg-cyan-dark" data-testid="sub-cta">{t("lp.sub.cta")} <ArrowRight className="ml-1 h-4 w-4" /></Button></Link>
          <span className="text-xs text-slate-400">{t("lp.sub.addonNote")}</span>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ zeiterfassung (dark) */
function Zeiterfassung() {
  const { t } = useTranslation();
  const cols = t("lp.zeit.cols", { returnObjects: true });
  const metrics = [[t("lp.zeit.workingNow"), 48, "text-emerald-400", 0, ""], [t("lp.zeit.onBreak"), 7, "text-amber-400", 0, ""], [t("lp.zeit.missingCheckout"), 2, "text-red-400", 0, ""], [t("lp.zeit.hoursToday"), 426.5, "text-cyan", 1, " h"]];
  const rows = [
    ["Amir Hadžić", "RHEINWORK_Worker_0028", "INTERNAL", "Essen", "06:57"],
    ["Marko Petrović", "ELEKTROBALKAN_Worker_0014", "SUB", "Essen", "07:02"],
  ];
  return (
    <section id="zeiterfassung" className="relative overflow-hidden bg-[#0B1120] py-24 text-white">
      <div className="digital-grid absolute inset-0 opacity-60" aria-hidden />
      <div className="absolute inset-0" style={{ background: "radial-gradient(80% 50% at 50% 0%, rgba(6,182,212,0.12), transparent 60%)" }} aria-hidden />
      <div className="relative mx-auto max-w-7xl px-5">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="flex items-center gap-2"><Badge tone="amber">{t("lp.zeit.badge")}</Badge><span className="font-heading text-sm font-bold text-white">{t("lp.zeit.perYear")}</span></div>
            <h2 className="mt-3 max-w-2xl font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">{t("lp.zeit.title")}</h2>
            <p className="mt-3 max-w-2xl text-slate-300">{t("lp.zeit.sub")}</p>
            <div className="mt-4 inline-flex max-w-2xl items-start gap-3 rounded-2xl border border-cyan/30 bg-cyan/10 p-3" data-testid="lp-zeit-geo">
              <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-cyan" />
              <div><div className="font-heading text-sm font-bold text-white">{t("lp.zeit.geoTitle")}</div><div className="text-xs text-slate-300">{t("lp.zeit.geoDesc")}</div></div>
            </div>
          </div>
        </div>

        <motion.div {...fade(0.1)} className="mt-10 rounded-3xl border border-white/10 bg-white/5 p-5 backdrop-blur-xl premium-shadow">
          <div className="grid gap-3 sm:grid-cols-4">
            {metrics.map(([l, v, c, dec, suf]) => (
              <div key={l} className="rounded-2xl border border-white/10 bg-navy/40 p-4">
                <div className={`font-heading text-3xl font-extrabold ${c}`}><CountUp to={v} decimals={dec} suffix={suf} /></div>
                <div className="mt-1 text-xs text-slate-400">{l}</div>
              </div>
            ))}
          </div>
          <div className="mt-4 overflow-x-auto rounded-2xl border border-white/10">
            <table className="w-full min-w-max text-left text-sm">
              <thead className="bg-white/5 text-[10px] uppercase tracking-wider text-slate-400">
                <tr>{cols.map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r[1]} className="border-t border-white/5">
                    <td className="px-4 py-3 font-semibold text-white">{r[0]}</td>
                    <td className="px-4 py-3 font-mono text-[11px] text-slate-400">{r[1]}</td>
                    <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${r[2] === "INTERNAL" ? "bg-cyan/15 text-cyan" : "bg-teal/20 text-teal"}`}>{r[2]}</span></td>
                    <td className="px-4 py-3 text-slate-300">{r[3]}</td>
                    <td className="px-4 py-3 font-mono text-cyan">{r[4]}</td>
                    <td className="px-4 py-3"><span className="inline-flex items-center gap-1 text-emerald-400"><span className="live-dot inline-block h-1.5 w-1.5 rounded-full bg-emerald-400" /> {t("lp.zeit.working")}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ baustellenverwaltung */
function Baustellenverwaltung() {
  const { t } = useTranslation();
  const feats = t("lp.bau.feats", { returnObjects: true });
  return (
    <section id="baustellen" className="mx-auto max-w-7xl px-5 py-24">
      <div className="grid items-center gap-14 lg:grid-cols-2">
        <motion.div {...fade(0.1)} className="order-2 card-soft overflow-hidden p-0 premium-shadow lg:order-1">
          <div className="flex items-center gap-2 border-b border-slate-100 bg-slate-50 px-4 py-2.5">
            <MapPin className="h-4 w-4 text-cyan-dark" /><span className="text-xs font-semibold text-slate-500">Baustelle Essen · Cluster 14</span>
            <Badge tone="cyan"><span className="live-dot mr-1 inline-block h-1.5 w-1.5 rounded-full bg-emerald-500" /> {t("lp.bau.liveTag")}</Badge>
            <span className="ml-auto flex items-center gap-1 rounded-full bg-cyan/10 px-2 py-0.5 text-[9px] font-semibold text-cyan-dark"><MapPin className="h-2.5 w-2.5" /> Geofence · 150m</span>
          </div>
          <div className="grid grid-cols-3 gap-px bg-slate-100">
            {[[t("lp.bau.kWorkers"), "31"], [t("lp.bau.kToday"), "18"], [t("lp.bau.kScans"), "126"]].map(([l, v]) => (
              <div key={l} className="bg-white p-4 text-center"><div className="font-heading text-2xl font-extrabold text-navy">{v}</div><div className="mt-1 text-[10px] text-slate-400">{l}</div></div>
            ))}
          </div>
          <div className="p-5">
            {[["Amir Hadžić", "INTERNAL", "07:02"], ["Marko Petrović", "SUB", "07:15"], ["Ivan Novak", "INTERNAL", "07:20"]].map((r) => (
              <div key={r[0]} className="flex items-center justify-between border-b border-slate-50 py-2 text-sm last:border-0">
                <div className="flex items-center gap-2"><span className="font-medium text-navy">{r[0]}</span><span className={`rounded-full px-2 py-0.5 text-[9px] font-semibold ${r[1] === "INTERNAL" ? "bg-cyan/10 text-cyan-dark" : "bg-teal/15 text-teal"}`}>{r[1]}</span></div>
                <span className="font-mono text-xs text-slate-500">{r[2]}</span>
              </div>
            ))}
            <div className="flex items-center justify-between border-t border-slate-50 py-2 text-sm">
              <div className="flex items-center gap-2"><span className="font-medium text-navy">Josip Barić</span><span className="rounded-full bg-red-100 px-2 py-0.5 text-[9px] font-semibold text-red-600">AUSSERHALB · 1.2 km</span></div>
              <MapPin className="h-3.5 w-3.5 text-red-500" />
            </div>
          </div>
        </motion.div>
        <motion.div {...fade(0)} className="order-1 lg:order-2">
          <div className="flex items-center gap-2"><Badge tone="cyan">{t("lp.bau.badge")}</Badge><span className="font-heading text-sm font-bold text-navy">{t("lp.bau.inclusive")}</span></div>
          <h2 className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-navy sm:text-4xl">{t("lp.bau.title")}</h2>
          <p className="mt-4 text-slate-500">{t("lp.bau.desc")}</p>
          <div className="mt-6 grid gap-2 sm:grid-cols-2">
            {feats.map((f) => <div key={f} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600"><Check className="h-4 w-4 shrink-0 text-teal" /> {f}</div>)}
          </div>
          <Link to="/register"><Button className="mt-7 rounded-full bg-navy text-white hover:bg-navy-muted" data-testid="bau-lp-cta">{t("lp.bau.cta")} <ArrowRight className="ml-1 h-4 w-4" /></Button></Link>
        </motion.div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ NFC flow */
function NfcFlow() {
  const { t } = useTranslation();
  const icons = [MapPin, Nfc, Smartphone, FileCheck2];
  const steps = t("lp.how.steps", { returnObjects: true });
  return (
    <section id="how" className="mx-auto max-w-7xl px-5 py-24">
      <SectionHead overline={t("lp.how.overline")} title={t("lp.how.title")} sub={t("lp.how.sub")} />
      <div className="mt-12 grid items-center gap-8 lg:grid-cols-[1fr_auto_1fr_auto_1fr]">
        <motion.div {...fade(0)} className="mx-auto w-56 rounded-3xl border-2 border-navy bg-white p-5 text-center premium-shadow" data-testid="nfc-tag">
          <div className="rounded-xl bg-navy px-3 py-2"><span className="font-heading text-lg font-extrabold text-white">Employ</span><span className="font-heading text-lg font-extrabold text-cyan">Guard</span><div className="text-[8px] tracking-[0.3em] text-slate-400">ZEITERFASSUNG</div></div>
          <div className="my-4 flex items-center justify-center gap-3">
            <Nfc className="h-8 w-8 text-cyan-dark" />
            <div className="grid grid-cols-4 gap-0.5">{Array.from({ length: 16 }).map((_, i) => <span key={i} className={`h-2 w-2 ${[0,1,3,4,6,9,11,12,15].includes(i) ? "bg-navy" : "bg-transparent"}`} />)}</div>
          </div>
          <div className="font-mono text-sm font-bold text-navy">EG-000184</div>
        </motion.div>
        <ChevronRight className="mx-auto hidden h-6 w-6 text-slate-300 lg:block" />
        <motion.div {...fade(0.1)} className="mx-auto w-56 rounded-[2rem] border-[6px] border-navy bg-white p-3 premium-shadow" data-testid="nfc-phone">
          <div className="rounded-t-xl bg-slate-100 px-3 py-1 text-center text-[10px] text-slate-400">employguard.de</div>
          <div className="rounded-b-xl bg-white px-3 py-4 text-center">
            <div className="text-xs font-semibold text-navy">Baustelle Essen</div>
            <div className="mt-3 font-semibold text-navy">Amir Hadžić</div>
            <div className="font-mono text-[10px] text-slate-400">RHEINWORK_Worker_0028</div>
            <div className="mt-3 text-[11px] text-slate-400">{t("lp.how.pin")}</div>
            <div className="font-mono text-lg tracking-[0.3em] text-navy">••••••</div>
            <div className="mt-3 rounded-full bg-cyan px-3 py-2 text-xs font-bold text-white">{t("lp.how.arbeitsstart")}</div>
          </div>
        </motion.div>
        <ChevronRight className="mx-auto hidden h-6 w-6 text-slate-300 lg:block" />
        <motion.div {...fade(0.2)} className="mx-auto w-56 rounded-2xl border border-slate-200 bg-white p-5 premium-shadow" data-testid="nfc-result">
          <div className="flex items-center gap-2 text-emerald-600"><BadgeCheck className="h-5 w-5" /> <span className="text-sm font-bold">{t("lp.how.success")}</span></div>
          <div className="mt-3 font-mono text-3xl font-extrabold text-navy">06:57</div>
          <div className="text-xs uppercase tracking-wider text-slate-400">CLOCK_IN · Essen</div>
          <div className="mt-3 h-px bg-slate-100" />
          <div className="mt-3 text-[11px] text-slate-500">{t("lp.how.recorded")}</div>
        </motion.div>
      </div>

      <div className="mt-16 grid gap-6 lg:grid-cols-4">
        {steps.map(([tt, d], i) => {
          const Icon = icons[i];
          return (
            <motion.div key={tt} {...fade(i * 0.06)} className="card-soft p-6">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-navy to-cyan-dark text-white"><Icon className="h-5 w-5" /></div>
              <div className="mt-4 font-heading font-bold text-navy">{tt}</div>
              <div className="mt-1 text-sm text-slate-500">{d}</div>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ hardware */
function Hardware() {
  const { t } = useTranslation();
  const flow = t("lp.hwSec.flow", { returnObjects: true });
  const tags = t("lp.hwSec.tags", { returnObjects: true });
  const ids = ["EG-000184", "EG-000185"];
  return (
    <section id="hardware" className="bg-slate-50 py-24">
      <div className="mx-auto max-w-7xl px-5">
        <SectionHead overline={t("lp.hwSec.overline")} title={t("lp.hwSec.title")} sub={t("lp.hwSec.sub")} />

        <div className="mt-12 grid gap-6 md:grid-cols-2">
          {tags.map(([tt, d], i) => (
            <motion.div key={ids[i]} {...fade(i * 0.08)} className="card-soft flex items-center gap-5 p-6" data-testid={`hw-tag-${i}`}>
              <div className="w-32 shrink-0 rounded-2xl border-2 border-navy bg-white p-3 text-center">
                <div className="rounded bg-navy py-1"><span className="font-heading text-xs font-extrabold text-white">Employ</span><span className="font-heading text-xs font-extrabold text-cyan">Guard</span></div>
                <div className="my-2 flex items-center justify-center gap-1.5"><Nfc className="h-5 w-5 text-cyan-dark" /><div className="grid grid-cols-3 gap-0.5">{Array.from({length:9}).map((_,j)=><span key={j} className={`h-1.5 w-1.5 ${[0,2,4,6,8].includes(j)?"bg-navy":"bg-transparent"}`}/>)}</div></div>
                <div className="text-[8px] tracking-[0.2em] text-slate-400">ZEITERFASSUNG</div>
                <div className="font-mono text-[10px] font-bold text-navy">{ids[i]}</div>
              </div>
              <div>
                <div className="font-heading text-lg font-bold text-navy">{tt}</div>
                <div className="mt-1 text-sm text-slate-500">{d}</div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-12">
          <div className="mb-3 text-center font-heading text-lg font-bold text-navy">{t("lp.hwSec.readyTitle")}</div>
          <p className="mx-auto mb-6 max-w-2xl text-center text-sm text-slate-500">{t("lp.hwSec.readyDesc")}</p>
          <div className="flex flex-wrap items-center justify-center gap-2" data-testid="hw-order-flow">
            {flow.map((f, i) => (
              <div key={f} className="flex items-center gap-2">
                <span className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600">{f}</span>
                {i < flow.length - 1 && <ChevronRight className="h-4 w-4 text-slate-300" />}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 grid gap-6 lg:grid-cols-2">
          <div className="card-soft p-6" data-testid="hw-price">
            <div className="font-heading text-lg font-bold text-navy">{t("lp.hwSec.priceTitle")}</div>
            <div className="mt-3 flex items-end gap-2"><span className="font-heading text-4xl font-extrabold text-navy">€2.00</span><span className="mb-1 text-sm text-slate-400">{t("lp.hwSec.perTag")}</span></div>
            <div className="mt-1 text-sm text-slate-500">{t("lp.hwSec.shippingLine")}</div>
            <div className="mt-4 rounded-xl bg-slate-50 px-4 py-3 text-xs text-slate-500">{t("lp.hwSec.priceNote")}</div>
          </div>
          <div className="card-soft overflow-hidden p-0" data-testid="hw-tag-mgmt">
            <div className="border-b border-slate-100 bg-slate-50 px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-400">{t("lp.hwSec.tagMgmt")}</div>
            {[["EG-000184", "Baustelle Essen · Main Baucontainer", 84, 72, 31], ["EG-000185", "Baustelle Dortmund · Gate A", 54, 48, 22]].map((r) => (
              <div key={r[0]} className="flex items-center justify-between border-b border-slate-100 px-4 py-3 last:border-0">
                <div><div className="font-mono text-sm font-bold text-navy">{r[0]}</div><div className="text-xs text-slate-400">{r[1]}</div></div>
                <div className="flex gap-4 text-center text-[11px]">
                  <div><div className="font-bold text-navy">{r[2]}</div><div className="text-slate-400">{t("lp.hwSec.scans")}</div></div>
                  <div><div className="font-bold text-navy">{r[3]}</div><div className="text-slate-400">{t("lp.hwSec.clocks")}</div></div>
                  <div><div className="font-bold text-navy">{r[4]}</div><div className="text-slate-400">{t("lp.hwSec.workers")}</div></div>
                  <Badge tone="emerald">{t("lp.hwSec.active")}</Badge>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ one tag / access */
function OneTagAccess() {
  const { t } = useTranslation();
  const allow = t("lp.ota.allow", { returnObjects: true });
  return (
    <section className="mx-auto max-w-7xl px-5 py-24">
      <div className="grid items-center gap-14 lg:grid-cols-2">
        <motion.div {...fade(0)}>
          <SectionHead overline={t("lp.ota.overline")} title={t("lp.ota.title")} sub={t("lp.ota.sub")} />
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="worker-pin-block">
            <div className="flex items-center gap-2 font-semibold text-navy"><KeyRound className="h-4 w-4 text-cyan-dark" /> {t("lp.ota.pinTitle")}</div>
            <p className="mt-2 text-sm text-slate-500">{t("lp.ota.pinDesc")}</p>
            <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
              <span className="font-mono text-navy">RHEINWORK_Worker_0028</span>
              <span className="font-mono tracking-[0.3em] text-slate-400">••••••</span>
            </div>
            <p className="mt-3 text-xs text-slate-500">{t("lp.ota.disableNote")}</p>
          </div>
        </motion.div>

        <motion.div {...fade(0.1)} className="card-soft p-6" data-testid="one-tag-diagram">
          <div className="mx-auto mb-5 w-fit rounded-2xl border-2 border-navy bg-white px-4 py-2 text-center"><div className="font-mono text-sm font-bold text-navy">EG-000184</div><div className="text-[10px] text-slate-400">Baustelle Essen</div></div>
          <div className="grid gap-3 sm:grid-cols-3">
            {[["RheinWork", ["Amir", "Milan", "Ivan"]], ["Elektro Balkan", ["Marko", "Nikola"]], ["FiberPro", ["Denis", "Emir"]]].map(([c, ws]) => (
              <div key={c} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="text-xs font-bold text-navy">{c}</div>
                <div className="mt-2 space-y-1">{ws.map((w) => <div key={w} className="flex items-center gap-1 text-xs text-slate-600"><span className="h-1.5 w-1.5 rounded-full bg-cyan" /> {w}</div>)}</div>
              </div>
            ))}
          </div>
          <div className="mt-5 rounded-xl bg-navy/5 p-4">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">{t("lp.ota.accessTitle")}</div>
            <div className="flex flex-wrap gap-2 text-xs">
              {allow.map((a) => <span key={a} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 font-medium text-emerald-700"><Check className="h-3 w-3" /> {a}</span>)}
              <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 font-medium text-red-700"><Ban className="h-3 w-3" /> Nova Elektro</span>
            </div>
            <p className="mt-2 text-[11px] text-slate-500">{t("lp.ota.accessNote")}</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ compliance + report */
function Compliance() {
  const { t } = useTranslation();
  const icons = [Lock, FileCheck2, Coffee, AlertTriangle, Timer, ScanLine];
  const caps = t("lp.comp.caps", { returnObjects: true });
  const cols = t("lp.comp.cols", { returnObjects: true });
  return (
    <section className="bg-slate-50 py-24">
      <div className="mx-auto max-w-7xl px-5">
        <SectionHead overline={t("lp.comp.overline")} title={t("lp.comp.title")} />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {caps.map(([tt, d], i) => {
            const Icon = icons[i];
            return (
              <motion.div key={tt} {...fade(i * 0.05)} className="card-soft p-6">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-navy/5 text-cyan-dark"><Icon className="h-5 w-5" /></div>
                <div className="mt-4 font-heading font-bold text-navy">{tt}</div>
                <div className="mt-1 text-sm text-slate-500">{d}</div>
              </motion.div>
            );
          })}
        </div>

        <motion.div {...fade(0.1)} className="mt-12 grid items-center gap-10 lg:grid-cols-2">
          <div>
            <h3 className="font-heading text-2xl font-bold text-navy">{t("lp.comp.zollTitle")}</h3>
            <p className="mt-3 text-slate-500">{t("lp.comp.zollDesc")}</p>
            <Button className="mt-6 rounded-full bg-navy text-white hover:bg-navy-muted" data-testid="zoll-cta">{t("lp.comp.zollCta")}</Button>
          </div>
          <div className="card-soft overflow-hidden p-0 premium-shadow" data-testid="zoll-report">
            <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-5 py-3">
              <div><span className="font-heading font-bold text-navy">EmployGuard</span> <span className="text-xs text-slate-400">Arbeitszeitnachweis</span></div>
              <FileText className="h-4 w-4 text-slate-400" />
            </div>
            <div className="px-5 py-4 text-xs text-slate-500">
              <div className="grid grid-cols-3 gap-2">
                <div><div className="text-slate-400">{t("lp.comp.repCompany")}</div><div className="font-medium text-navy">RheinWork GmbH</div></div>
                <div><div className="text-slate-400">{t("lp.comp.repBaustelle")}</div><div className="font-medium text-navy">Essen Cluster 14</div></div>
                <div><div className="text-slate-400">{t("lp.comp.repPeriod")}</div><div className="font-medium text-navy">August 2026</div></div>
              </div>
              <table className="mt-4 w-full text-[11px]">
                <thead className="text-slate-400"><tr>{cols.map(h=><th key={h} className="py-1 text-left">{h}</th>)}</tr></thead>
                <tbody className="text-navy">
                  {[["01.08","Amir Hadžić","06:57","16:02","0:45","8:20"],["01.08","Marko Petrović","07:02","15:33","0:30","8:01"]].map((r,i)=>(
                    <tr key={i} className="border-t border-slate-100">{r.map((c,j)=><td key={j} className="py-1.5">{c}</td>)}</tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-3 rounded-lg bg-slate-50 px-3 py-2 font-mono text-[10px] text-slate-500">
                <div>Report ID: EG-RPT-2026-08-0442</div>
                <div className="truncate">SHA-256: 9f2c1a7b8e34d0c5a1f6b93e7d21c8fa4b0e6d9c2a5f18730be24c1d905e6a3f</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ financials */
function Financials() {
  const { t } = useTranslation();
  return (
    <section className="mx-auto max-w-7xl px-5 py-24">
      <SectionHead overline={t("lp.fin.overline")} title={t("lp.fin.title")} sub={t("lp.fin.sub")} />
      <div className="mt-12 grid items-stretch gap-6 lg:grid-cols-2">
        <motion.div {...fade(0)} className="card-soft p-6" data-testid="fin-worker">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">{t("lp.fin.workerCalc")}</div>
          <div className="mt-1 font-heading text-lg font-bold text-navy">Marko Petrović <span className="text-sm font-medium text-slate-400">· Elektro Balkan</span></div>
          <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
            {[[t("lp.fin.hours"),"8.5"],[t("lp.fin.costRate"),"€27/h"],[t("lp.fin.custRate"),"€44/h"],[t("lp.fin.cost"),"€229.50"],[t("lp.fin.revenue"),"€374.00"],[t("lp.fin.contribution"),"€144.50"]].map(([k,v],i)=>(
              <div key={k} className={`rounded-xl px-3 py-2.5 ${i===5?"bg-emerald-50":"bg-slate-50"}`}><div className="text-[11px] text-slate-400">{k}</div><div className={`font-semibold ${i===5?"text-emerald-600":"text-navy"}`}>{v}</div></div>
            ))}
          </div>
        </motion.div>
        <motion.div {...fade(0.1)} className="card-soft p-6" data-testid="fin-project">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">{t("lp.fin.projectTitle")}</div>
          <div className="mt-1 font-heading text-lg font-bold text-navy">Essen FTTH Cluster 14</div>
          <div className="mt-4 space-y-2 text-sm">
            {[[t("lp.fin.hours"),"1,890 (1,020h + 870h sub)"],[t("lp.fin.revenue"),"€77,000"],[t("lp.fin.cost"),"€49,000"],[t("lp.fin.contribution"),"€28,000"]].map(([k,v])=>(
              <div key={k} className="flex justify-between border-b border-slate-100 pb-2"><span className="text-slate-400">{k}</span><span className="font-semibold text-navy">{v}</span></div>
            ))}
          </div>
          <div className="mt-4 flex items-center justify-between rounded-xl bg-gradient-to-r from-cyan-dark to-teal px-4 py-3 text-white">
            <span className="flex items-center gap-1 text-sm font-semibold"><TrendingUp className="h-4 w-4" /> {t("lp.fin.marginLabel")}</span>
            <span className="font-heading text-2xl font-extrabold">36.4%</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ connected map (SVG) */
export function ConnectedMap() {
  const { t } = useTranslation();
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const CX = 450, CY = 260;
  const labels = t("lp.conn.nodes", { returnObjects: true });
  const pos = [{ x: 130, y: 70 }, { x: 130, y: 190 }, { x: 130, y: 330 }, { x: 130, y: 450 }, { x: 770, y: 70 }, { x: 770, y: 190 }, { x: 770, y: 330 }, { x: 770, y: 450 }];
  const nodes = labels.map((l, i) => ({ a: l[0], b: l[1], ...pos[i] }));
  return (
    <div ref={ref} data-testid="connected-map">
      <svg viewBox="0 0 900 520" className="h-auto w-full" role="img" aria-label="EmployGuard connected platform map">
        <defs>
          <linearGradient id="cm-line" x1="0" y1="0" x2="1" y2="0"><stop offset="0%" stopColor="#06B6D4" /><stop offset="100%" stopColor="#0D9488" /></linearGradient>
          <radialGradient id="cm-core" cx="50%" cy="50%" r="50%"><stop offset="0%" stopColor="#06B6D4" stopOpacity="0.35" /><stop offset="100%" stopColor="#06B6D4" stopOpacity="0" /></radialGradient>
        </defs>
        <circle cx={CX} cy={CY} r="120" fill="url(#cm-core)" />
        {nodes.map((n, i) => {
          const mx = (CX + n.x) / 2;
          const d = `M ${CX} ${CY} C ${mx} ${CY}, ${mx} ${n.y}, ${n.x} ${n.y}`;
          return (
            <g key={`l-${i}`}>
              <motion.path d={d} fill="none" stroke="url(#cm-line)" strokeWidth="2" strokeLinecap="round"
                initial={{ pathLength: 0, opacity: 0.15 }} animate={inView ? { pathLength: 1, opacity: 0.55 } : {}}
                transition={{ duration: 1.1, delay: reduce ? 0 : 0.15 + i * 0.08, ease: "easeInOut" }} />
              <path d={d} fill="none" stroke="#22D3EE" strokeWidth="1.5" strokeLinecap="round" className={reduce ? "" : "flow-line"} opacity="0.5" />
            </g>
          );
        })}
        {nodes.map((n, i) => (
          <motion.g key={`n-${i}`} initial={{ opacity: 0, scale: 0.8 }} animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.4, delay: reduce ? 0 : 0.4 + i * 0.08 }} style={{ transformOrigin: `${n.x}px ${n.y}px`, transformBox: "fill-box" }}>
            <rect x={n.x - (n.x < CX ? 118 : 2)} y={n.y - 22} width="120" height="44" rx="12" fill="rgba(255,255,255,0.06)" stroke="rgba(34,211,238,0.25)" />
            <circle cx={n.x} cy={n.y} r="4" fill="#22D3EE" />
            <text x={n.x < CX ? n.x - 108 : n.x + 12} y={n.y - 3} fill="#fff" fontSize="13" fontWeight="700">{n.a}</text>
            <text x={n.x < CX ? n.x - 108 : n.x + 12} y={n.y + 13} fill="#94A3B8" fontSize="10">{n.b}</text>
          </motion.g>
        ))}
        <g>
          <rect x={CX - 90} y={CY - 26} width="180" height="52" rx="26" fill="#06B6D4" fillOpacity="0.12" stroke="rgba(34,211,238,0.5)" />
          <text x={CX} y={CY + 6} textAnchor="middle" fill="#22D3EE" fontSize="20" fontWeight="800" fontFamily="Outfit, sans-serif">EmployGuard</text>
        </g>
      </svg>
    </div>
  );
}

function Connected() {
  const { t } = useTranslation();
  return (
    <section className="bg-[#0B1120] py-24 text-white">
      <div className="mx-auto max-w-7xl px-5">
        <SectionHead dark center overline={t("lp.conn.overline")} title={t("lp.conn.title")} sub={t("lp.conn.sub")} />
        <div className="mt-8"><ConnectedMap /></div>
        <div className="mt-6 flex flex-wrap justify-center gap-2 font-mono text-xs text-slate-400">
          <span className="rounded-full bg-white/5 px-3 py-1">RHEINWORK_Worker_0028</span>
          <span className="rounded-full bg-white/5 px-3 py-1">ELEKTROBALKAN_Worker_0014</span>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ industries + use case */
function Industries() {
  const { t } = useTranslation();
  const icons = [HardHat, Cable, Cpu, Truck, ScanLine, Factory, UtensilsCrossed, Users, HeartPulse];
  const list = t("lp.ind.list", { returnObjects: true });
  const tags = t("lp.ind.tags", { returnObjects: true });
  return (
    <section className="mx-auto max-w-7xl px-5 py-24">
      <SectionHead overline={t("lp.ind.overline")} title={t("lp.ind.title")} />
      <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-3">
        {list.map((l, i) => {
          const Icon = icons[i];
          return (
            <motion.div key={l} {...fade(i * 0.03)} className="group flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 transition-colors hover:border-cyan/40 hover:bg-cyan/5">
              <Icon className="h-5 w-5 text-cyan-dark transition-transform group-hover:scale-110" /> {l}
            </motion.div>
          );
        })}
      </div>

      <motion.div {...fade(0.1)} className="mt-12 rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-8 premium-shadow" data-testid="usecase">
        <h3 className="font-heading text-2xl font-bold text-navy">{t("lp.ind.useTitle")}</h3>
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          {[["RheinWork GmbH", 21], ["Elektro Balkan", 18], ["FiberPro", 8]].map(([c, n]) => (
            <div key={c} className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="font-heading text-3xl font-extrabold text-navy"><CountUp to={n} /></div>
              <div className="text-sm text-slate-500">{c} · {t("lp.ind.useSuffix")}</div>
            </div>
          ))}
        </div>
        <div className="mt-5 flex flex-wrap gap-2 text-xs text-slate-500">
          {tags.map((x) => <span key={x} className="rounded-full border border-slate-200 bg-white px-3 py-1">{x}</span>)}
        </div>
      </motion.div>
    </section>
  );
}

/* ------------------------------------------------------------------ security */
function Security() {
  const { t } = useTranslation();
  const items = t("lp.sec.items", { returnObjects: true });
  return (
    <section id="security" className="mx-auto max-w-7xl px-5 py-24">
      <div className="card-soft overflow-hidden">
        <div className="grid gap-0 md:grid-cols-2">
          <div className="bg-gradient-to-br from-navy to-cyan-dark p-10 text-white">
            <Lock className="h-8 w-8 text-cyan" />
            <h2 className="mt-4 font-heading text-3xl font-extrabold">{t("lp.sec.title")}</h2>
            <p className="mt-3 text-slate-200">{t("lp.sec.desc")}</p>
          </div>
          <div className="grid grid-cols-1 gap-px bg-slate-100 sm:grid-cols-2">
            {items.map((it) => <div key={it} className="flex items-center gap-3 bg-white p-5 text-sm font-medium text-slate-700"><ShieldCheck className="h-4 w-4 text-teal" /> {it}</div>)}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ pricing */
const PLAN_PRICES = {
  starter: { monthly: 49.99, annual: 539.89, twoY: 839.83, effAnnual: 44.99, effTwoY: 34.99 },
  pro: { monthly: 129.99, annual: 1403.89, twoY: 2183.83, effAnnual: 116.99, effTwoY: 90.99 },
  unlimited: { monthly: 159.99, annual: 1727.89, twoY: 2687.83, effAnnual: 143.99, effTwoY: 111.99 },
};

const PLAN_META = {
  starter: { nameKey: "starterName", featsKey: "starterFeats", ctaKey: "starterCta", badgeKey: "upTo10", testid: "starter" },
  pro: { nameKey: "proName", featsKey: "proFeats", ctaKey: "proCta", badgeKey: "recommended", testid: "professional" },
  unlimited: { nameKey: "unlimitedName", featsKey: "unlFeats", ctaKey: "unlCta", badgeKey: "unlBadge", testid: "unlimited" },
};

function PlanCard({ t, fmt, cycle, plan, data, highlight, saveBadge }) {
  const price = cycle === "monthly" ? data.monthly : cycle === "annual" ? data.annual : data.twoY;
  const eff = cycle === "annual" ? data.effAnnual : cycle === "twoY" ? data.effTwoY : null;
  const unit = cycle === "monthly" ? t("lp.price.perMonth") : cycle === "annual" ? t("lp.price.perYear") : t("lp.price.per2y");
  const m = PLAN_META[plan];
  const feats = t(`lp.price.${m.featsKey}`, { returnObjects: true });
  const badgeCls = plan === "starter" ? "bg-slate-100 text-slate-500" : "bg-cyan text-white";
  return (
    <div className={`card-soft relative flex flex-col p-8 ${highlight ? "ring-2 ring-cyan premium-shadow lg:-mt-4 lg:mb-4" : ""}`} data-testid={`price-${m.testid}`}>
      <div className="mb-3 flex items-center gap-2">
        <span className={`inline-flex rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wider ${badgeCls}`}>{t(`lp.price.${m.badgeKey}`)}</span>
        {saveBadge && <span className="inline-flex rounded-full bg-teal/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-teal">{saveBadge}</span>}
      </div>
      <div className="font-heading text-xl font-bold text-navy">{t(`lp.price.${m.nameKey}`)}</div>
      <div className="mt-4 flex items-end gap-1">
        <span className="font-heading text-4xl font-extrabold text-navy" data-testid={`price-${m.testid}-amount`}>{fmt(price)}</span>
        <span className="mb-1 text-base font-medium text-slate-400">{unit}</span>
      </div>
      <div className="mt-1 text-xs font-medium text-slate-400">{t("lp.price.vat")}</div>
      {eff && <div className="mt-1 text-xs font-semibold text-teal">{t("lp.price.effPrefix")} {fmt(eff)} {t("lp.price.perMonth")}</div>}
      <ul className="mt-6 flex-1 space-y-3">
        {feats.map((f) => <li key={f} className="flex items-center gap-2 text-sm text-slate-600"><Check className="h-4 w-4 shrink-0 text-teal" /> {f}</li>)}
      </ul>
      {plan === "starter" && <div className="mt-4 rounded-xl bg-slate-50 px-4 py-3 text-xs font-medium text-slate-500">{t("lp.price.starterNoAddons")}</div>}
      <Link to="/register" className="mt-7"><Button className={`w-full rounded-full ${highlight ? "bg-cyan text-white hover:bg-cyan-dark" : "bg-navy text-white hover:bg-navy-muted"}`} data-testid={`price-${m.testid}-cta`}>{t(`lp.price.${m.ctaKey}`)}</Button></Link>
    </div>
  );
}

function AddonCard({ t, keyName, price, avail, name, feats }) {
  return (
    <div className="card-soft flex flex-col p-7" data-testid={`addon-${keyName}`}>
      <div className="flex items-center justify-between">
        <div className="font-heading text-lg font-bold text-navy">{name}</div>
        <span className="rounded-full bg-cyan/10 px-3 py-1 text-sm font-bold text-cyan-dark">{price}</span>
      </div>
      <div className="mt-1 flex items-center gap-1 text-xs font-medium text-slate-400"><Lock className="h-3 w-3" /> {avail} · {t("lp.price.vat")}</div>
      <ul className="mt-5 grid flex-1 gap-2 sm:grid-cols-2">
        {feats.map((f) => <li key={f} className="flex items-center gap-2 text-sm text-slate-600"><Check className="h-4 w-4 shrink-0 text-teal" /> {f}</li>)}
      </ul>
    </div>
  );
}

function Pricing() {
  const { t, i18n } = useTranslation();
  const [cycle, setCycle] = useState("monthly");
  const fmt = (n) => new Intl.NumberFormat(i18n.language === "en" ? "en-IE" : "de-DE", { style: "currency", currency: "EUR", minimumFractionDigits: 2 }).format(n);
  const saveBadge = cycle === "annual" ? t("lp.price.save10") : cycle === "twoY" ? t("lp.price.save30") : null;
  const tabs = [["monthly", t("lp.price.billMonthly")], ["annual", t("lp.price.billAnnual")], ["twoY", t("lp.price.bill2y")]];
  return (
    <section id="pricing" className="bg-slate-50 py-24">
      <div className="mx-auto max-w-7xl px-5">
        <SectionHead center overline={t("lp.price.overline")} title={t("lp.price.title")} sub={t("lp.price.sub")} />
        <div className="mt-8 flex justify-center">
          <div className="inline-flex flex-wrap justify-center gap-1 rounded-full border border-slate-200 bg-white p-1" data-testid="billing-selector">
            {tabs.map(([k, label]) => (
              <button key={k} onClick={() => setCycle(k)} data-testid={`billing-${k}`}
                className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${cycle === k ? "bg-navy text-white" : "text-slate-500 hover:text-navy"}`}>{label}</button>
            ))}
          </div>
        </div>
        <div className="mx-auto mt-12 grid max-w-6xl items-start gap-6 lg:grid-cols-3">
          <PlanCard t={t} fmt={fmt} cycle={cycle} plan="starter" data={PLAN_PRICES.starter} highlight={false} saveBadge={cycle === "monthly" ? null : saveBadge} />
          <PlanCard t={t} fmt={fmt} cycle={cycle} plan="pro" data={PLAN_PRICES.pro} highlight saveBadge={cycle === "monthly" ? null : saveBadge} />
          <PlanCard t={t} fmt={fmt} cycle={cycle} plan="unlimited" data={PLAN_PRICES.unlimited} highlight={false} saveBadge={cycle === "monthly" ? null : saveBadge} />
        </div>

        <div className="mt-16">
          <div className="mb-6 text-center">
            <div className="font-heading text-xl font-bold text-navy">{t("lp.price.addonTitle")}</div>
            <div className="mt-1 text-sm text-slate-500">{t("lp.price.addonSub")}</div>
          </div>
          <div className="grid gap-6 lg:grid-cols-2">
            <AddonCard t={t} keyName="subunternehmer" price={t("lp.price.subPrice")} avail={t("lp.price.subAvail")} name={t("lp.price.subName")} feats={t("lp.price.subFeats", { returnObjects: true })} />
            <AddonCard t={t} keyName="zeiterfassung" price={t("lp.price.zeitPrice")} avail={t("lp.price.zeitAvail")} name={t("lp.price.zeitName")} feats={t("lp.price.zeitFeats", { returnObjects: true })} />
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 text-center" data-testid="hw-price-card">
          <span className="font-heading font-bold text-navy">{t("lp.price.hwName")}</span>
          <span className="ml-2 text-slate-500">{t("lp.price.hwRest")}</span>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ faq */
function Faq() {
  const { t } = useTranslation();
  const items = t("lp.faq.items", { returnObjects: true });
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="mx-auto max-w-3xl px-5 py-24" data-testid="faq-section">
      <motion.div {...fade()} className="text-center">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-dark">{t("lp.faq.overline")}</div>
        <h2 className="mt-3 font-heading text-3xl font-extrabold text-navy sm:text-4xl">{t("lp.faq.title")}</h2>
      </motion.div>
      <div className="mt-10 space-y-3">
        {items.map((it, i) => (
          <motion.div key={i} {...fade(i * 0.04)} className="card-soft overflow-hidden">
            <button
              onClick={() => setOpen(open === i ? -1 : i)}
              data-testid={`faq-q-${i}`}
              className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
            >
              <span className="font-heading text-base font-bold text-navy">{it.q}</span>
              <ChevronRight className={`h-5 w-5 shrink-0 text-cyan-dark transition-transform ${open === i ? "rotate-90" : ""}`} />
            </button>
            {open === i && (
              <div className="px-5 pb-5 text-sm leading-relaxed text-slate-600" data-testid={`faq-a-${i}`}>{it.a}</div>
            )}
          </motion.div>
        ))}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ final cta + journey */
function FinalCTA() {
  const { t } = useTranslation();
  const journey = t("lp.final.journey", { returnObjects: true });
  return (
    <section className="mx-auto max-w-7xl px-5 py-24">
      <div className="mb-12 overflow-x-auto">
        <div className="flex min-w-max items-center gap-2" data-testid="journey">
          {journey.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-600">{s}</div>
              {i < journey.length - 1 && <ChevronRight className="h-4 w-4 text-slate-300" />}
            </div>
          ))}
        </div>
      </div>
      <div className="hero-navy relative overflow-hidden rounded-3xl px-8 py-16 text-center">
        <HeroBackground />
        <div className="relative">
          <h2 className="mx-auto max-w-2xl font-heading text-3xl font-extrabold text-white sm:text-4xl">{t("lp.final.t1")}<br />{t("lp.final.t2")}</h2>
          <p className="mx-auto mt-4 max-w-xl text-slate-300">{t("lp.final.sub")}</p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <Link to="/register"><Button size="lg" data-testid="final-cta-btn" className="w-full rounded-full bg-cyan px-8 text-white transition-transform hover:bg-cyan-dark hover:scale-[1.02] active:scale-[0.98] sm:w-auto">{t("lp.final.primary")} <ArrowRight className="ml-1 h-4 w-4" /></Button></Link>
            <a href="#platform"><Button size="lg" variant="outline" className="w-full rounded-full border-white/20 bg-transparent px-8 text-white hover:bg-white/10 hover:text-white sm:w-auto">{t("lp.final.secondary")}</Button></a>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Footer() {
  const { t } = useTranslation();
  return (
    <footer className="border-t border-slate-200 bg-white">
      <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 px-5 py-10 sm:flex-row sm:items-center">
        <Logo />
        <div className="flex flex-wrap gap-6 text-sm text-slate-500">
          <Link to="/foreign-workforce" className="hover:text-navy">{t("lp.foot.fw")}</Link>
          <Link to="/subunternehmer" className="hover:text-navy">{t("lp.foot.sub")}</Link>
          <Link to="/zeiterfassung" className="hover:text-navy">{t("lp.foot.zeit")}</Link>
          <Link to="/nfc-zeiterfassung" className="hover:text-navy">{t("lp.foot.nfc")}</Link>
          <Link to="/zeiterfassung-pflicht-2026" className="hover:text-navy">{t("lp.foot.pl2026")}</Link>
          <Link to="/ratgeber" className="hover:text-navy">{t("lp.foot.rg")}</Link>
          <Link to="/hardware" className="hover:text-navy">{t("lp.foot.hw")}</Link>
          <Link to="/privacy" className="hover:text-navy">{t("lp.foot.privacy")}</Link>
          <Link to="/imprint" className="hover:text-navy">{t("lp.foot.imprint")}</Link>
          <a href="mailto:info@employguard.de" className="hover:text-navy">info@employguard.de</a>
        </div>
        <div className="text-xs text-slate-400">© {new Date().getFullYear()} EmployGuard.</div>
      </div>
    </footer>
  );
}

function InternationalRecruitment() {
  const { t } = useTranslation();
  const steps = [
    { icon: FileSignature, label: t("im.land.s1") },
    { icon: GraduationCap, label: t("im.land.s2") },
    { icon: Stamp, label: t("im.land.s3") },
    { icon: Ticket, label: t("im.land.s4") },
    { icon: PlaneTakeoff, label: t("im.land.s5") },
    { icon: DoorOpen, label: t("im.land.s6") },
  ];
  const paths = ["WESTERN_BALKANS", "SKILLED_WORKER", "EU_BLUE_CARD", "RECOGNITION_16D", "RECOGNITION_PARTNERSHIP", "FAST_TRACK_81A"];
  return (
    <section id="international" className="relative overflow-hidden bg-[#0B1120] py-24 text-white">
      <div className="pointer-events-none absolute inset-0 opacity-40" style={{ background: "radial-gradient(600px 300px at 80% 10%, rgba(34,211,238,.15), transparent)" }} />
      <div className="relative mx-auto max-w-7xl px-5">
        <div className="mb-14 max-w-2xl">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-cyan/30 bg-cyan/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-cyan">{t("im.land.kicker")}</span>
          <h2 className="mt-4 font-heading text-3xl font-extrabold sm:text-4xl">{t("im.land.title")}</h2>
          <p className="mt-4 text-base text-slate-300">{t("im.land.subtitle")}</p>
        </div>

        <div className="mb-14 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-6">
          {steps.map((s, i) => (
            <div key={i} className="group relative rounded-2xl border border-white/10 bg-white/5 p-4 text-center backdrop-blur transition-all hover:border-cyan/40" data-testid={`land-imm-step-${i}`}>
              <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-cyan/15 text-cyan"><s.icon className="h-5 w-5" /></div>
              <div className="text-xs font-semibold text-slate-200">{s.label}</div>
              {i < steps.length - 1 && <div className="absolute right-[-8px] top-1/2 hidden h-px w-4 -translate-y-1/2 bg-gradient-to-r from-cyan/50 to-transparent md:block" />}
            </div>
          ))}
        </div>

        <div className="mb-14 flex flex-wrap gap-2">
          {paths.map((p) => <span key={p} className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs font-medium text-slate-200">{t(`im.p.${p}`)}</span>)}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {[["svc1", "im.land.svc1"], ["svc2", "im.land.svc2"]].map(([k, key]) => (
            <div key={k} className="rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur" data-testid={`land-service-${k}`}>
              <div className="font-heading text-lg font-bold text-white">{t(key)}</div>
              <div className="mt-1 text-sm text-cyan">{t("im.land.svcPrice")}</div>
              <p className="mt-3 text-sm text-slate-300">{t(`${key}Desc`)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ContractGenerator() {
  const steps = ["Mitarbeiter", "Vertragsdaten prüfen", "Fehlende Angaben ergänzen", "EmployGuard AI", "PDF + Word", "Unterschrift", "Compliance / Einreise"];
  const highlights = ["Arbeitgeberdaten automatisch übernehmen", "Mitarbeiterdaten automatisch übernehmen", "Fehlende Angaben gezielt abfragen", "Gehalt oder Stundenlohn", "Urlaub, Arbeitszeit, Probezeit", "Zuschläge & Tarifvertrag", "PDF & Word", "Versionshistorie & Audit"];
  return (
    <section id="contracts" className="mx-auto max-w-7xl px-5 py-24" data-testid="land-contracts">
      <div className="mb-12 max-w-2xl">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-cyan/30 bg-cyan/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-cyan-dark">Strukturierte Vertragserstellung mit EmployGuard AI</span>
        <h2 className="mt-4 font-heading text-3xl font-extrabold text-navy sm:text-4xl">Arbeitsverträge schneller erstellen – mit strukturierten Unternehmens- und Mitarbeiterdaten</h2>
        <p className="mt-4 text-base text-slate-600">EmployGuard nutzt bereits gespeicherte Informationen und fragt nur fehlende Vertragsdetails ab. Pflichtangaben werden geprüft, bevor der Entwurf erstellt wird.</p>
      </div>
      <div className="mb-12 flex flex-wrap items-center gap-2">
        {steps.map((s, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-navy shadow-sm" data-testid={`land-ct-step-${i}`}>{s}</div>
            {i < steps.length - 1 && <ChevronRight className="h-4 w-4 text-cyan" />}
          </div>
        ))}
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {highlights.map((h, i) => (
          <div key={i} className="flex items-start gap-2 rounded-2xl border border-slate-200 bg-white p-4"><FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-cyan-dark" /><span className="text-sm text-slate-600">{h}</span></div>
        ))}
      </div>
    </section>
  );
}

export default function Landing() {
  const { t } = useTranslation();
  return (
    <div className="bg-background" data-testid="landing-v2">
      <Seo titleKey="seo.homeT" descKey="seo.homeD" path="/" faq={t("lp.faq.items", { returnObjects: true })} schema={{
        "@context": "https://schema.org", "@type": "SoftwareApplication", name: "EmployGuard",
        applicationCategory: "BusinessApplication", operatingSystem: "Web", description: t("seo.homeD"),
        offers: [
          { "@type": "Offer", name: "Starter", price: "49.99", priceCurrency: "EUR", category: "Subscription" },
          { "@type": "Offer", name: "Professional", price: "159.99", priceCurrency: "EUR", category: "Subscription" },
        ],
      }} />
      <Header />
      <Hero />
      <ForeignWorkforce />
      <InternationalRecruitment />
      <ContractGenerator />
      <Industries />
      <Modules />
      <Subunternehmer />
      <Zeiterfassung />
      <PflegeSection />
      <Baustellenverwaltung />
      <NfcFlow />
      <Hardware />
      <OneTagAccess />
      <Compliance />
      <Financials />
      <Connected />
      <Security />
      <Pricing />
      <Faq />
      <FinalCTA />
      <Footer />
    </div>
  );
}
